import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MarketplaceAppSelect from '@tenant/core/App/Select/MarketplaceAppSelect';
import ExtensionPointEnum from 'constants/ExtensionPointEnum';

const HOOK_TYPE_ACTION = 'action';
const HOOK_TYPE_WEBHOOK = 'webhook';

// Confirmed business rule (owner-approved): only the two synchronous "Before" lifecycle
// events are Action Hooks — a direct HTTP GET the platform calls and waits on before
// proceeding. Every other event is an asynchronous Webhook (POST). Keep these values in
// step with ExtensionPoint: 1 = BeforeAppSubscribed, 2 = BeforeAppUnsubscribed.
const ACTION_HOOK_EXTENSION_POINTS = [1, 2];

const defaultValues = {
  EndpointUrl: '',
  ExtensionPointId: '',
  RequestHeaders: '',
  AppId: null,
  IsAppSpecific: true,
  // New self-serve registrations default to Webhook (async POST) — the common case;
  // Action Hook is opt-in via the Hook Type toggle. (The BE entity default of true only
  // applies to rows not created through this form, so the two defaults intentionally differ.)
  IsActionHook: false,
};

const FieldLabel = ({ label, tooltip }) => (
  <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    {label}
    <Tooltip title={tooltip} placement='right' arrow>
      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
    </Tooltip>
  </Box>
);

const WebhookRegistrationForm = ({
  visible,
  loading,
  handleClose,
  handleSubmit,
  initialValues,
  isEdit = false,
}) => {
  const [values, setValues] = useState(defaultValues);
  const [errors, setErrors] = useState({});
  const [appliesToAllApps, setAppliesToAllApps] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [hookType, setHookType] = useState(HOOK_TYPE_WEBHOOK);

  const httpMethod = hookType === HOOK_TYPE_ACTION ? 'GET' : 'POST';

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        const isAction = initialValues.IsActionHook ?? true;
        setValues({
          EndpointUrl: initialValues.EndpointUrl ?? '',
          ExtensionPointId: initialValues.ExtensionPointId ?? '',
          RequestHeaders: initialValues.RequestHeaders ?? '',
          AppId: initialValues.AppId ?? null,
          IsAppSpecific: initialValues.IsAppSpecific ?? true,
          IsActionHook: isAction,
        });
        setHookType(isAction ? HOOK_TYPE_ACTION : HOOK_TYPE_WEBHOOK);
        setAppliesToAllApps(!initialValues.IsAppSpecific);
        setSelectedApp(
          initialValues.AppId
            ? { Id: initialValues.AppId, Name: initialValues.AppName ?? '' }
            : null,
        );
      } else {
        setValues(defaultValues);
        setErrors({});
        setHookType(HOOK_TYPE_WEBHOOK);
        setAppliesToAllApps(false);
        setSelectedApp(null);
      }
    }
  }, [visible, initialValues]);

  const validate = () => {
    const newErrors = {};

    if (!values.EndpointUrl) {
      newErrors.EndpointUrl = 'Endpoint URL is required';
    } else if (!/^https:\/\//i.test(values.EndpointUrl)) {
      newErrors.EndpointUrl = 'Endpoint URL must use HTTPS';
    }

    if (values.ExtensionPointId === '' || values.ExtensionPointId === null) {
      newErrors.ExtensionPointId = 'Event is required';
    }

    if (!appliesToAllApps && !selectedApp) {
      newErrors.AppId = 'Select a Marketplace App or check "Applies to all apps"';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = () => {
    if (!validate()) return;

    const payload = {
      EndpointUrl: values.EndpointUrl,
      ExtensionPointId: Number(values.ExtensionPointId),
      HttpMethod: httpMethod,
      RequestHeaders: values.RequestHeaders || null,
      AppId: appliesToAllApps ? null : selectedApp?.Id ?? null,
      IsAppSpecific: !appliesToAllApps,
      IsActionHook: hookType === HOOK_TYPE_ACTION,
    };

    if (isEdit && initialValues?.Id) {
      payload.id = initialValues.Id;
    }

    handleSubmit(payload);
  };

  const handleChange = (field) => (e) => {
    setValues((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleHookTypeChange = (_e, value) => {
    setHookType(value);
    setValues((prev) => {
      const id = Number(prev.ExtensionPointId);
      const isActionOnly = ACTION_HOOK_EXTENSION_POINTS.includes(id);
      const keep =
        value === HOOK_TYPE_ACTION ? isActionOnly : !isActionOnly;
      return keep ? prev : { ...prev, ExtensionPointId: '' };
    });
    setErrors((prev) => ({ ...prev, ExtensionPointId: undefined }));
  };

  const handleAllAppsChange = (e) => {
    const checked = e.target.checked;
    setAppliesToAllApps(checked);
    if (checked) {
      setSelectedApp(null);
      setErrors((prev) => ({ ...prev, AppId: undefined }));
    }
  };

  return (
    <Dialog open={visible} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit Webhook Registration' : 'Add Webhook Registration'}
      </DialogTitle>
      <DialogContent>
        <Box display='flex' flexDirection='column' gap={2} mt={1}>

          {/* Hook type */}
          <FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <FieldLabel
                label='Hook Type'
                tooltip='Action Hook makes a direct HTTP GET call when the event fires. Webhook sends a POST payload to your endpoint.'
              />
            </Box>
            <RadioGroup
              row
              value={hookType}
              onChange={handleHookTypeChange}
              aria-label='Hook Type'
            >
              <FormControlLabel
                value={HOOK_TYPE_ACTION}
                control={<Radio />}
                label='Action Hook (GET)'
              />
              <FormControlLabel
                value={HOOK_TYPE_WEBHOOK}
                control={<Radio />}
                label='Webhook (POST)'
              />
            </RadioGroup>
          </FormControl>

          {/* Endpoint URL */}
          <Tooltip
            title='The HTTPS URL your service exposes to receive this callback. Must start with https://.'
            placement='right'
            arrow
          >
            <TextField
              label='Endpoint URL'
              required
              fullWidth
              value={values.EndpointUrl}
              onChange={handleChange('EndpointUrl')}
              error={!!errors.EndpointUrl}
              helperText={errors.EndpointUrl ?? `HTTP method will be ${httpMethod}`}
              placeholder='https://your-endpoint.example.com/webhook'
              inputProps={{ 'aria-label': 'Endpoint URL' }}
            />
          </Tooltip>

          {/* Event */}
          <Tooltip
            title='The platform event that will trigger this callback. Choose from the list of available extension points.'
            placement='right'
            arrow
          >
            <FormControl fullWidth required error={!!errors.ExtensionPointId}>
              <InputLabel id='extension-point-label'>Event</InputLabel>
              <Select
                labelId='extension-point-label'
                label='Event'
                value={values.ExtensionPointId}
                onChange={handleChange('ExtensionPointId')}
                inputProps={{ 'aria-label': 'Event' }}
              >
                {ExtensionPointEnum.filter((ep) =>
                hookType === HOOK_TYPE_ACTION
                  ? ACTION_HOOK_EXTENSION_POINTS.includes(ep.Value)
                  : !ACTION_HOOK_EXTENSION_POINTS.includes(ep.Value),
              ).map((ep) => (
                  <MenuItem key={ep.Value} value={ep.Value}>
                    {ep.Label}
                  </MenuItem>
                ))}
              </Select>
              {errors.ExtensionPointId && (
                <FormHelperText>{errors.ExtensionPointId}</FormHelperText>
              )}
            </FormControl>
          </Tooltip>

          {/* Request Headers */}
          <Tooltip
            title='Optional. A JSON object of custom HTTP headers to include with each callback request, e.g. {"X-Api-Key": "secret"}.'
            placement='right'
            arrow
          >
            <TextField
              label='Request Headers (optional JSON)'
              fullWidth
              multiline
              minRows={3}
              value={values.RequestHeaders}
              onChange={handleChange('RequestHeaders')}
              placeholder='{"X-Api-Key": "secret"}'
              inputProps={{ 'aria-label': 'Request Headers' }}
            />
          </Tooltip>

          {/* Applies to all apps */}
          <Tooltip
            title='When checked, this registration applies to events from every Marketplace app. Leave unchecked to scope it to a single app.'
            placement='right'
            arrow
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={appliesToAllApps}
                  onChange={handleAllAppsChange}
                  inputProps={{ 'aria-label': 'Applies to all apps' }}
                />
              }
              label='Applies to all apps'
            />
          </Tooltip>

          {/* Marketplace App */}
          <Tooltip
            title='The specific Marketplace app whose events will trigger this callback. Disabled when "Applies to all apps" is checked.'
            placement='right'
            arrow
          >
            {/* Wrapper div needed: Tooltip requires a single forwardRef child; Autocomplete is not one */}
            <div>
              <MarketplaceAppSelect
                disabled={appliesToAllApps}
                value={selectedApp}
                onChange={(e, value) => {
                  setSelectedApp(value);
                  setErrors((prev) => ({ ...prev, AppId: undefined }));
                }}
                error={!!errors.AppId}
                helperText={errors.AppId}
                textFieldProps={{
                  label: 'Marketplace App',
                  required: !appliesToAllApps,
                }}
              />
            </div>
          </Tooltip>

        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading} autoFocus>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={onSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(WebhookRegistrationForm);
